import { AnyAction, Reducer, Store, applyMiddleware, compose, createStore } from 'redux';
import createSageMiddleware, { SagaIterator } from 'redux-saga';
import { withExtraArgument } from 'redux-thunk';

import { IApplicationContext } from '../../types/applicationContext';
import { IMakeRequest } from '../../utils/request';
import { setUserProfilingAvalablesEmails } from '../../utils/userProfilingAvalablesEmails';
import { setUserProfilingEnabled } from '../../utils/userProfilingEnabled';

import { TActions } from './actions';
import { IAppState, ISerializableAppState } from './app_state';

export interface IReducerAddedAction {
  type: '@@store/REDUCER_ADDED';
  name: string;
}

interface ICustomReducer extends Reducer<IAppState, AnyAction> {
  addReducer(reducer: Reducer<IAppState, AnyAction>): void;
}

function createCustomReducer(): ICustomReducer {
  const subReducers: Reducer<IAppState, AnyAction>[] = [];

  const reducer = ((state: IAppState, action: TActions) => {
    return subReducers.reduce((acc, subReducer) => {
      return subReducer(acc, action);
    }, state);
  }) as ICustomReducer;

  reducer.addReducer = (subReducer: Reducer<IAppState, AnyAction>) => {
    subReducers.push(subReducer);
  };

  return reducer;
}

export interface IAppStore extends Store<IAppState, AnyAction> {
  runSaga(saga: () => SagaIterator): void;
  addReducer(reducer: Reducer<IAppState, AnyAction>): void;
  serialize(): ISerializableAppState;
}

/**
 * Legacy
 * @deprecated
 */
interface ILegacyParameters {
  makeRequest: IMakeRequest;

  optionalParams?: {
    dispachReducerAddedAction?: boolean;
  };
}

export function createAppStore(
  { logger, config, httpApi }: IApplicationContext,
  initialState: ISerializableAppState,
  { makeRequest, optionalParams }: ILegacyParameters,
): IAppStore {
  const initializedParams = Object.assign(
    {
      dispachReducerAddedAction: true,
    },
    optionalParams || {},
  );

  const { dispachReducerAddedAction } = initializedParams;

  const state = {
    ...initialState,
    makeRequest,
    httpApi,
    logger,
    config,
  };

  const sagaMiddleware = createSageMiddleware();

  const customReducer = createCustomReducer();

  const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const thunkMiddleware = withExtraArgument({ httpApi, logger });
  const store = createStore(customReducer, state, composeEnhancers(applyMiddleware(thunkMiddleware, sagaMiddleware)));

  function addReducer(reducer: Reducer<IAppState, AnyAction>) {
    customReducer.addReducer(reducer);

    if (dispachReducerAddedAction) {
      store.dispatch({
        type: '@@store/REDUCER_ADDED',
        name: reducer.name,
      });
    }
  }

  function runSaga(saga: () => SagaIterator) {
    sagaMiddleware.run(saga);
  }

  function serialize(): ISerializableAppState {
    const currentState = Object.assign({}, store.getState());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (currentState as any).makeRequest;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (currentState as any).httpApi;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (currentState as any).logger;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (currentState as any).config;

    return currentState;
  }

  /**
   * @todo Удалить строку с RS
   * @description Данный функционал появился в задаче CD-112916, будет удалён в задаче CD-117944
   */
  setUserProfilingEnabled(!!config.get<boolean>('Commercial.UserProfiling.Enabled'));

  /**
   * @todo Удалить строку с RS
   * @description Данный функционал появился в задаче CD-133271, будет удалён в задаче CD-117944
   */
  setUserProfilingAvalablesEmails(config.get<string[]>('Commercial.UserProfiling.AvailableEmails') || undefined);

  return { ...store, runSaga, addReducer, serialize };
}
